import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import type { HistoryVersion } from '@ast-data/shared';
import { authFetch } from '../../utils/authFetch';
import { httpErrorOf } from '../../utils/httpError';
import { loadFailureOf, type LoadFailure } from '../../components/loadFailure';

/** Which change failed, as the `history.error.*` messages name it. */
type HistoryCall = 'clear' | 'delete' | 'restore';

/**
 * Version history for any entity. `apiBase` is the entity's API root
 * (e.g. '/api/entries' or '/api/projects'); the standard history routes hang
 * off `${apiBase}/${entityId}/history`.
 *
 * History is online-only (server-derived, not replicated), so every call can
 * fail. A failed read surfaces as `loadFailure`, for a LoadErrorAlert; a failed
 * change as the sentence in `error`. Changes also return a boolean so callers
 * can react.
 */
export function useHistory(apiBase: string, entityId: string | undefined) {
    const intl = useIntl();
    const [versions, setVersions] = useState<HistoryVersion[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /** Kept while a retry runs, so the alert stays until the answer is in. */
    const [loadFailure, setLoadFailure] = useState<LoadFailure | null>(null);

    /** One change; a refusal or a lost connection becomes `error`. */
    const request = useCallback(
        async (
            path: string,
            method: string | undefined,
            call: HistoryCall
        ): Promise<Response | null> => {
            try {
                const response = await authFetch(
                    `${apiBase}/${entityId}${path}`,
                    method ? { method } : undefined
                );
                if (!response.ok) {
                    setError(
                        intl.formatMessage(
                            { id: `history.error.${call}` },
                            { status: response.status }
                        )
                    );
                    return null;
                }
                return response;
            } catch {
                setError(
                    intl.formatMessage({ id: `history.error.${call}Network` })
                );
                return null;
            }
        },
        [apiBase, entityId, intl]
    );

    const refresh = useCallback(async () => {
        if (!entityId) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await authFetch(`${apiBase}/${entityId}/history`);
            if (!response.ok) {
                setLoadFailure(loadFailureOf(await httpErrorOf(response, '')));
                return;
            }
            setVersions(await response.json());
            setLoadFailure(null);
        } catch (err) {
            setLoadFailure(loadFailureOf(err));
        } finally {
            setLoading(false);
        }
    }, [apiBase, entityId]);

    /** A change to the history, then the list read afresh. */
    const change = useCallback(
        async (
            path: string,
            method: string,
            call: HistoryCall
        ): Promise<boolean> => {
            if (!entityId) {
                return false;
            }
            setError(null);
            if (!(await request(path, method, call))) {
                return false;
            }
            await refresh();
            return true;
        },
        [entityId, request, refresh]
    );

    const clearHistory = useCallback(
        () => change('/history', 'DELETE', 'clear'),
        [change]
    );
    const deleteVersion = useCallback(
        (historyId: string) =>
            change(`/history/${historyId}`, 'DELETE', 'delete'),
        [change]
    );
    const restoreVersion = useCallback(
        (historyId: string) =>
            change(`/restore/${historyId}`, 'POST', 'restore'),
        [change]
    );

    useEffect(() => {
        // Plain data fetching: `refresh` flips its own loading flag before the
        // request goes out. Not the derived state the rule is aimed at.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        refresh();
    }, [refresh]);

    return {
        versions,
        loading,
        error,
        loadFailure,
        refresh,
        clearHistory,
        deleteVersion,
        restoreVersion
    };
}
